import { TDynamicFormConfig } from "@/features/Edit/dynamic-edit-form";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import i18n from "@/i18n";
import { Control } from "react-hook-form";

import { AutocompleteAsyncField } from "@/features/FormCard/Fields";
import {
  Option,
  DEFAULT_ASSET_CLASS_OPTIONS_BUILDER,
} from "../../../features/Edit/common";
import { z } from "zod";

function ConcentratedAssetField(control: Control<any>) {
  return (
    <AutocompleteAsyncField
      name="concentrated_asset"
      label={i18n.t("a6_static_data.columns.concentrated_asset", {
        ns: "tables",
        defaultValue: "Actif concentré du client (indiqué le code client)",
      })}
      control={control}
      base_url="a6/a6_portfolios"
      column_name="client"
    />
  );
}

const concentrated_asset_conf = {
  label: i18n.t("a6_static_data.columns.concentrated_asset", {
    ns: "tables",
    defaultValue: "Actif concentré du client",
  }),
  validator: z
    .preprocess(
      preprocessTextAsyncAutocompleteField,
      z.union([
        // we recv / send as string the data
        z.string().nullable().optional(),

        // for the autocomplete, the form state is an object
        z.object({
          text: z.string(),
          id: z.number(),
          account_type: z.string(),
          currency: z.string(),
        }),
      ]),
    )
    .optional()
    .nullable(),
  field: ConcentratedAssetField,
};

// Example of a dynamic form config
export const dynamicFormConfig: TDynamicFormConfig = {
  id: z.number(),
  has_transactions: z.boolean().optional().nullable(),
  has_time_series: z.boolean().optional().nullable(),
  name: z
    .string()
    .min(1, "Le nom est obligatoire")
    .transform((val) => val.toUpperCase()),
  isin: z
    .string()
    .refine(
      (val) => val === "" || val === null || val.length === 12,
      "L'ISIN doit comporter  12 caractères",
    )
    .nullable()
    .optional(),
  code: z.string().nullable().optional(),
  ticker: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  issuer_id: z.coerce.number().optional().nullable(),
  a6_issuer_name: z
    .preprocess(
      preprocessTextAsyncAutocompleteField,
      z.union([
        // we recv / send as string the data
        z.string().nullable().optional(),

        // for the autocomplete, the form state is an object
        z.object({
          text: z.string(),
          id: z.number(),
        }),
      ]),
    )
    .optional(),
  // a6_issuer_name: z.string().nullable().optional(),
  strategy_id: z.coerce.number().optional().nullable(),
  asset_class_2: {
    key_config: {
      validator: {
        asset_class_1: z.string().nullable().optional(),
        asset_class_2: z.string().nullable().optional(),
      },
      primary_key: "asset_class_1",
      secondary_key: "asset_class_2",
    },
    select: {
      equity: {
        stock: "stock",
        stock_fund: "stock_fund",
        structured_product: "structured_product",
        stock_option: "stock_option",
      },
      art: {
        art: "art",
        jewel: "jewel",
        vehicle: "vehicle",
      },
      alternative: {
        crypto: "crypto",
        hedge_fund: "hedge_fund",
        hedge_fund_unlisted: "hedge_fund_unlisted",
        precious_metal: "precious_metal",
        other_alternative_listed: "other_alternative_listed",
        other_alternative_non_listed: "other_alternative_non_listed",
      },
      real_estate: {
        re_fund: "re_fund",
        building: "building",
        personal_use_real_estate: "personal_use_real_estate",
        scpi_usufruit: "scpi_usufruit",
      },
      cash: {
        cash: "cash",
        fonds_euros: "fonds_euros",
        monetary_fund: "monetary_fund",
      },
      fixed_income: {
        private_debt: "private_debt",
        relatives_debt: "relatives_debt",
        fixed_income_fund: "fixed_income_fund",
        bond: "bond",
        fixed_income_structured_product: "fixed_income_structured_product",
      },
      loan: {
        home_loan: "home_loan",
        other_loan: "other_loan",
      },
      private_equity: {
        private_equity_fund: "private_equity_fund",
        private_equity_direct: "private_equity_direct", // Corrresponds to "Participations"
      },
      diversified_investments: {
        diversified_fund: "diversified_fund",
      },
    },
    section: {
      stock: {
        concentrated_asset: concentrated_asset_conf,
      },
      stock_fund: {
        concentrated_asset: concentrated_asset_conf,
      },
      art: {
        concentrated_asset: concentrated_asset_conf,
      },
      jewel: {
        concentrated_asset: concentrated_asset_conf,
      },
      crypto: {
        concentrated_asset: concentrated_asset_conf,
      },
      hedge_fund: {
        concentrated_asset: concentrated_asset_conf,
      },
      hedge_fund_unlisted: {
        concentrated_asset: concentrated_asset_conf,
      },
      precious_metal: {
        concentrated_asset: concentrated_asset_conf,
      },
      other_alternative_listed: {
        concentrated_asset: concentrated_asset_conf,
      },
      other_alternative_non_listed: {
        concentrated_asset: concentrated_asset_conf,
      },
      // Other loan
      other_loan: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
        loan_amount_granted: {
          label: "Montant du prêt accordé",
          validator: z.coerce.number().optional().nullable(),
        },
        loan_with_collateral: {
          label: "Prêt consenti avec nantissement",
          validator: z.boolean().optional().nullable(),
        },
      },
      // Cash
      cash: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        concentrated_asset: concentrated_asset_conf,
      },
      // Private debt
      private_debt: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
        concentrated_asset: concentrated_asset_conf,
      },
      // PE fund
      private_equity_fund: {
        commitment: {
          label: "Engagement Initial",
          validator: z.coerce.number().optional().nullable(),
        },
        capital_callable: {
          label: "Capital Exigible",
          validator: z.boolean().optional().nullable(),
        },
        concentrated_asset: concentrated_asset_conf,
      },
      private_equity_direct: {
        commitment: {
          label: "Engagement Initial",
          validator: z.coerce.number().optional().nullable(),
        },
        concentrated_asset: concentrated_asset_conf,
      },
      // Building
      building: {
        owner: {
          label: "Propriétaire",
          validator: z.string().optional().nullable(),
        },
        property_type: {
          label: "Type de bien",
          validator: z.string().optional().nullable(),
        },
        property_size: {
          label: "Taille du bien",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_own_funds: {
          label: "Financement fonds propres",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_mortage: {
          label: "Financement hypothécaire",
          validator: z.coerce.number().optional().nullable(),
        },
        concentrated_asset: concentrated_asset_conf,
      },
      // Personal use real estate
      personal_use_real_estate: {
        owner: {
          label: "Propriétaire",
          validator: z.string().optional().nullable(),
        },
        property_type: {
          label: "Type de bien",
          validator: z.string().optional().nullable(),
        },
        property_size: {
          label: "Taille du bien",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_own_funds: {
          label: "Financement fonds propres",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_mortage: {
          label: "Financement hypothécaire",
          validator: z.coerce.number().optional().nullable(),
        },
        concentrated_asset: concentrated_asset_conf,
      },
      // Real Estate Fund
      re_fund: {
        owner: {
          label: "Propriétaire",
          validator: z.string().optional().nullable(),
        },
        property_type: {
          label: "Type de bien",
          validator: z.string().optional().nullable(),
        },
        property_size: {
          label: "Taille du bien",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_own_funds: {
          label: "Financement fonds propres",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_mortage: {
          label: "Financement hypothécaire",
          validator: z.coerce.number().optional().nullable(),
        },
        concentrated_asset: concentrated_asset_conf,
      },
      // Bonds
      bond: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
        concentrated_asset: concentrated_asset_conf,
      },
      // Home loan
      home_loan: {
        owner: {
          label: "Propriétaire",
          validator: z.string().optional().nullable(),
        },
        property_id: {
          label: "ID de la propriété",
          validator: z.coerce.number().optional().nullable(),
        },
        loan_amount_granted: {
          label: "Montant prêt accordé",
          validator: z.coerce.number().optional().nullable(),
        },
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
      },
      // Family loans
      relatives_debt: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
        concentrated_asset: concentrated_asset_conf,
      },
      // Structured products
      structured_product: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        concentrated_asset: concentrated_asset_conf,
      },
      // Stock options
      stock_option: {
        stock_option_strike: {
          label: "Prix d'exercice",
          validator: z.coerce.number().optional().nullable(),
        },
        concentrated_asset: concentrated_asset_conf,
      },
      fonds_euros: {
        concentrated_asset: concentrated_asset_conf,
      },
      monetary_fund: {
        concentrated_asset: concentrated_asset_conf,
      },
      fixed_income_fund: {
        concentrated_asset: concentrated_asset_conf,
      },
    },
  },
};

export const DEFAULT_ASSET_CLASS_OPTIONS: Option[] =
  DEFAULT_ASSET_CLASS_OPTIONS_BUILDER(dynamicFormConfig);

export const dynamicNewFormConfigWithoutId: Omit<TDynamicFormConfig, "id"> =
  (() => {
    const { id, ...configWithoutId } = dynamicFormConfig;
    return configWithoutId;
  })();
